import type { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import {
  NumberFilterOperator,
  type NumberValue,
} from "@redotech/redo-views-common/advanced-filters/number-filter";
import {
  SortDirection,
  type TableSort,
} from "@redotech/redo-views-common/table";
import * as z from "zod";
import { SupplyIntelBrandPricingSchema } from "./brand-pricing";
import {
  isSupplyIntelActionable,
  SUPPLY_INTEL_LOT_OR_LIQUIDATE_STEPS,
  SUPPLY_INTEL_NEEDS_REVIEW_STEPS,
  SUPPLY_INTEL_RESALE_STEPS,
  SupplyIntelAgeBand,
  type SupplyIntelBrandUnitRow,
  SupplyIntelChannel,
  supplyIntelLeverSellThrough,
  supplyIntelLiquidationFloorUsd,
  supplyIntelResaleBufferUsd,
  SupplyIntelSellThroughLever,
  supplyIntelValueUsd,
} from "./brand-units";
import type { SupplyIntelConfig } from "./config";
import { SupplyIntelGradeStatus, SupplyIntelNextStep } from "./next-step";
import { SupplyIntelPool } from "./pool";

const SupplyIntelValueSchema_ = z.object({
  currency: z.string().nullable(),
  amount: z.number(),
});
const SupplyIntelValueSchema: z.ZodType<SupplyIntelValue> =
  SupplyIntelValueSchema_;
interface SupplyIntelValue extends z.infer<typeof SupplyIntelValueSchema_> {}

/** USD amounts convert at the config's approximate rates and skip currencies without one. */
const SupplyIntelUnitTotalsSchema_ = z.object({
  /** On shelf plus in process. */
  units: z.number(),
  /** Actionable units (resale, off-price lot or liquidate) at store price. */
  actionableValueUsd: z.number(),
  /** What liquidating every actionable unit today recovers. */
  liquidationFloorUsd: z.number(),
  /** The extra reselling actionable units recoups over the floor, at the config's expected sell-through. */
  resaleBufferUsd: z.number(),
  /** `resaleBufferUsd` at 30% sell-through. */
  resaleBufferUsd30: z.number(),
  /** `resaleBufferUsd` at 50% sell-through. */
  resaleBufferUsd50: z.number(),
  /** `resaleBufferUsd` on the Redo marketplace scenario rather than eBay. */
  redoMarketplaceResaleBufferUsd: z.number(),
  redoMarketplaceResaleBufferUsd30: z.number(),
  redoMarketplaceResaleBufferUsd50: z.number(),
  /** Next step resale, new or pre-owned. */
  resaleUnits: z.number(),
  /** Next step off-price lot or liquidate. */
  lotOrLiquidateUnits: z.number(),
  keepOnBrandStoreUnits: z.number(),
  /** Next step brand rejected, needs grade, needs price or review hold. */
  needsReviewUnits: z.number(),
  /** Next step already routed. */
  alreadyRoutedUnits: z.number(),
  /** Next step excluded by category. */
  // Defaulted so an older server's response still parses during a deploy.
  excludedUnits: z.number().default(0),
  /** Products with no category, or "Uncategorized", so no category rule applies. */
  // Defaulted so an older server's response still parses during a deploy.
  noCategoryUnits: z.number().default(0),
  /** Staging, station, tote or transit rather than a bin. */
  inProcessUnits: z.number(),
  /** At or past the refulfillment timer. */
  agedUnits: z.number(),
  /** At or past `longAgedDays`. */
  longAgedUnits: z.number(),
  forSaleUnits: z.number(),
  /** Non-sellable bins outside the damaged area, plus sellable bins without a restock decision. */
  heldUnits: z.number(),
  damagedAreaUnits: z.number(),
  notSoldAsNewUnits: z.number(),
  /** Blank or unmapped grade, or one the brand flags for review. */
  needsGradeUnits: z.number(),
  rejectedUnits: z.number(),
  unpricedUnits: z.number(),
  /** One entry per currency, sorted by currency. */
  valueAtList: z.array(SupplyIntelValueSchema),
  valueAtListUsd: z.number(),
});
export const SupplyIntelUnitTotalsSchema: z.ZodType<SupplyIntelUnitTotals> =
  SupplyIntelUnitTotalsSchema_;
export interface SupplyIntelUnitTotals extends z.infer<
  typeof SupplyIntelUnitTotalsSchema_
> {}

const SupplyIntelBrandRowSchema_ = SupplyIntelUnitTotalsSchema_.extend({
  accountId: z.string(),
  teamId: z.string().nullable(),
  brandName: z.string().nullable(),
  /** The brand's own resale prices, when it has them; its totals use them. */
  brandPricing: SupplyIntelBrandPricingSchema.nullish(),
});
export const SupplyIntelBrandRowSchema: z.ZodType<SupplyIntelBrandRow> =
  SupplyIntelBrandRowSchema_;
export interface SupplyIntelBrandRow extends z.infer<
  typeof SupplyIntelBrandRowSchema_
> {}

/** The warehouse filter value for units with no known warehouse. */
export const SUPPLY_INTEL_NO_WAREHOUSE = "none";

const SupplyIntelWarehouseTotalsSchema_ = SupplyIntelUnitTotalsSchema_.extend({
  /** A warehouse ID, or {@link SUPPLY_INTEL_NO_WAREHOUSE}. */
  warehouseId: z.string(),
});

const SupplyIntelBrandListRowSchema_ = SupplyIntelBrandRowSchema_.extend({
  byWarehouse: z.array(SupplyIntelWarehouseTotalsSchema_),
});
/** A brand's row as the snapshot stores it, with its totals split by warehouse for the warehouse filter. */
export const SupplyIntelBrandListRowSchema: z.ZodType<SupplyIntelBrandListRow> =
  SupplyIntelBrandListRowSchema_;
export interface SupplyIntelBrandListRow extends z.infer<
  typeof SupplyIntelBrandListRowSchema_
> {}

const SupplyIntelWarehouseSchema_ = z.object({
  id: z.string(),
  name: z.string(),
});
export const SupplyIntelWarehouseSchema: z.ZodType<SupplyIntelWarehouse> =
  SupplyIntelWarehouseSchema_;
export interface SupplyIntelWarehouse extends z.infer<
  typeof SupplyIntelWarehouseSchema_
> {}

/** Each warehouse the rows are in, by name, with units of no known warehouse last. */
export function supplyIntelWarehouses(
  rows: readonly Pick<
    SupplyIntelBrandUnitRow,
    "warehouseId" | "warehouseName"
  >[],
): SupplyIntelWarehouse[] {
  const byId = new Map<string, string>();
  for (const row of rows) {
    const id = row.warehouseId ?? SUPPLY_INTEL_NO_WAREHOUSE;
    if (!byId.has(id)) {
      byId.set(
        id,
        row.warehouseId === null
          ? "No warehouse"
          : (row.warehouseName ?? `Warehouse ${id}`),
      );
    }
  }
  return [...byId]
    .map(([id, name]) => ({ id, name }))
    .sort(
      (a, b) =>
        Number(a.id === SUPPLY_INTEL_NO_WAREHOUSE) -
          Number(b.id === SUPPLY_INTEL_NO_WAREHOUSE) ||
        a.name.localeCompare(b.name),
    );
}

export enum SupplyIntelBrandSortKey {
  ActionableValueUsd = "actionableValueUsd",
  AgedUnits = "agedUnits",
  AlreadyRoutedUnits = "alreadyRoutedUnits",
  BrandName = "brandName",
  DamagedAreaUnits = "damagedAreaUnits",
  ExcludedUnits = "excludedUnits",
  ForSaleUnits = "forSaleUnits",
  HeldUnits = "heldUnits",
  InProcessUnits = "inProcessUnits",
  KeepOnBrandStoreUnits = "keepOnBrandStoreUnits",
  LiquidationFloorUsd = "liquidationFloorUsd",
  LongAgedUnits = "longAgedUnits",
  LotOrLiquidateUnits = "lotOrLiquidateUnits",
  NeedsGradeUnits = "needsGradeUnits",
  NeedsReviewUnits = "needsReviewUnits",
  NoCategoryUnits = "noCategoryUnits",
  NotSoldAsNewUnits = "notSoldAsNewUnits",
  RedoMarketplaceResaleBufferUsd = "redoMarketplaceResaleBufferUsd",
  RedoMarketplaceResaleBufferUsd30 = "redoMarketplaceResaleBufferUsd30",
  RedoMarketplaceResaleBufferUsd50 = "redoMarketplaceResaleBufferUsd50",
  RejectedUnits = "rejectedUnits",
  ResaleBufferUsd = "resaleBufferUsd",
  ResaleBufferUsd30 = "resaleBufferUsd30",
  ResaleBufferUsd50 = "resaleBufferUsd50",
  ResaleUnits = "resaleUnits",
  Units = "units",
  ValueAtListUsd = "valueAtListUsd",
}

export const DEFAULT_SUPPLY_INTEL_BRAND_SORT: TableSort<SupplyIntelBrandSortKey> =
  {
    key: SupplyIntelBrandSortKey.ResaleBufferUsd,
    direction: SortDirection.DESC,
  };

/** The totals field, and sort key, holding the resale buffer on each channel at each lever. */
export const SUPPLY_INTEL_RESALE_BUFFER_FIELD = {
  [SupplyIntelChannel.Ebay]: {
    [SupplyIntelSellThroughLever.Expected]:
      SupplyIntelBrandSortKey.ResaleBufferUsd,
    [SupplyIntelSellThroughLever.Thirty]:
      SupplyIntelBrandSortKey.ResaleBufferUsd30,
    [SupplyIntelSellThroughLever.Fifty]:
      SupplyIntelBrandSortKey.ResaleBufferUsd50,
  },
  [SupplyIntelChannel.RedoMarketplace]: {
    [SupplyIntelSellThroughLever.Expected]:
      SupplyIntelBrandSortKey.RedoMarketplaceResaleBufferUsd,
    [SupplyIntelSellThroughLever.Thirty]:
      SupplyIntelBrandSortKey.RedoMarketplaceResaleBufferUsd30,
    [SupplyIntelSellThroughLever.Fifty]:
      SupplyIntelBrandSortKey.RedoMarketplaceResaleBufferUsd50,
  },
} as const satisfies Record<
  SupplyIntelChannel,
  Record<SupplyIntelSellThroughLever, SupplyIntelBrandSortKey>
>;

export enum SupplyIntelBrandFilterName {
  AgedUnits = "agedUnits",
  Currency = "currency",
  HeldUnits = "heldUnits",
  LongAgedUnits = "longAgedUnits",
  /** Warehouse IDs, or {@link SUPPLY_INTEL_NO_WAREHOUSE}; scopes every total to those warehouses. */
  Warehouse = "warehouse",
}

type NumericTotals = Omit<SupplyIntelUnitTotals, "valueAtList">;

function emptyTotals(): NumericTotals {
  return {
    units: 0,
    resaleUnits: 0,
    actionableValueUsd: 0,
    liquidationFloorUsd: 0,
    resaleBufferUsd: 0,
    resaleBufferUsd30: 0,
    resaleBufferUsd50: 0,
    redoMarketplaceResaleBufferUsd: 0,
    redoMarketplaceResaleBufferUsd30: 0,
    redoMarketplaceResaleBufferUsd50: 0,
    lotOrLiquidateUnits: 0,
    keepOnBrandStoreUnits: 0,
    needsReviewUnits: 0,
    alreadyRoutedUnits: 0,
    excludedUnits: 0,
    noCategoryUnits: 0,
    inProcessUnits: 0,
    agedUnits: 0,
    longAgedUnits: 0,
    forSaleUnits: 0,
    heldUnits: 0,
    damagedAreaUnits: 0,
    notSoldAsNewUnits: 0,
    needsGradeUnits: 0,
    rejectedUnits: 0,
    unpricedUnits: 0,
    valueAtListUsd: 0,
  };
}

function addValue(
  valueByCurrency: Map<string | null, number>,
  currency: string | null,
  amount: number,
): void {
  valueByCurrency.set(currency, (valueByCurrency.get(currency) ?? 0) + amount);
}

function valueList(
  valueByCurrency: Map<string | null, number>,
): SupplyIntelValue[] {
  return [...valueByCurrency]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => (a.currency ?? "").localeCompare(b.currency ?? ""));
}

function addPool(totals: NumericTotals, row: SupplyIntelBrandUnitRow): void {
  switch (row.pool) {
    case SupplyIntelPool.ForSale:
      totals.forSaleUnits += row.units;
      return;
    case SupplyIntelPool.HeldNonSellable:
    case SupplyIntelPool.ReservedInSellableBin:
      totals.heldUnits += row.units;
      return;
    case SupplyIntelPool.DamagedArea:
      totals.damagedAreaUnits += row.units;
      return;
    case null:
      totals.inProcessUnits += row.units;
      return;
  }
}

function addGradeStatus(
  totals: NumericTotals,
  row: SupplyIntelBrandUnitRow,
): void {
  switch (row.gradeStatus) {
    case SupplyIntelGradeStatus.NotSoldAsNew:
      totals.notSoldAsNewUnits += row.units;
      return;
    case SupplyIntelGradeStatus.FlaggedForReview:
    case SupplyIntelGradeStatus.Unknown:
      totals.needsGradeUnits += row.units;
      return;
    case SupplyIntelGradeStatus.Rejected:
      totals.rejectedUnits += row.units;
      return;
    case SupplyIntelGradeStatus.SellAsNew:
      return;
  }
}

function addActionable(
  totals: NumericTotals,
  row: SupplyIntelBrandUnitRow,
  config: SupplyIntelConfig,
): void {
  if (!isSupplyIntelActionable(row.nextStep)) {
    return;
  }
  const buffer = (
    lever: SupplyIntelSellThroughLever,
    channel: SupplyIntelChannel,
  ) =>
    supplyIntelResaleBufferUsd(
      row,
      supplyIntelLeverSellThrough(lever, config),
      config,
      channel,
    );
  const redo = SupplyIntelChannel.RedoMarketplace;
  totals.actionableValueUsd += supplyIntelValueUsd(row, config) ?? 0;
  totals.liquidationFloorUsd +=
    supplyIntelLiquidationFloorUsd(row, config) ?? 0;
  const ebay = SupplyIntelChannel.Ebay;
  totals.resaleBufferUsd += buffer(SupplyIntelSellThroughLever.Expected, ebay);
  totals.resaleBufferUsd30 += buffer(SupplyIntelSellThroughLever.Thirty, ebay);
  totals.resaleBufferUsd50 += buffer(SupplyIntelSellThroughLever.Fifty, ebay);
  totals.redoMarketplaceResaleBufferUsd += buffer(
    SupplyIntelSellThroughLever.Expected,
    redo,
  );
  totals.redoMarketplaceResaleBufferUsd30 += buffer(
    SupplyIntelSellThroughLever.Thirty,
    redo,
  );
  totals.redoMarketplaceResaleBufferUsd50 += buffer(
    SupplyIntelSellThroughLever.Fifty,
    redo,
  );
}

function addNextStep(
  totals: NumericTotals,
  row: SupplyIntelBrandUnitRow,
  config: SupplyIntelConfig,
): void {
  const step = row.nextStep;
  if (step === null) {
    return;
  }
  if (SUPPLY_INTEL_RESALE_STEPS.has(step)) {
    totals.resaleUnits += row.units;
  } else if (SUPPLY_INTEL_LOT_OR_LIQUIDATE_STEPS.has(step)) {
    totals.lotOrLiquidateUnits += row.units;
  } else if (SUPPLY_INTEL_NEEDS_REVIEW_STEPS.has(step)) {
    totals.needsReviewUnits += row.units;
  } else if (step === SupplyIntelNextStep.KeepOnBrandStore) {
    totals.keepOnBrandStoreUnits += row.units;
  } else if (step === SupplyIntelNextStep.Excluded) {
    totals.excludedUnits += row.units;
  } else {
    totals.alreadyRoutedUnits += row.units;
  }
}

/** Totals over unit rows: a brand's, a warehouse's or a product's. */
export function totalSupplyIntelUnitRows(
  rows: readonly SupplyIntelBrandUnitRow[],
  config: SupplyIntelConfig,
): SupplyIntelUnitTotals {
  const totals = emptyTotals();
  const valueByCurrency = new Map<string | null, number>();
  for (const row of rows) {
    totals.units += row.units;
    if (row.ageBand !== SupplyIntelAgeBand.Fresh) {
      totals.agedUnits += row.units;
    }
    if (row.ageBand === SupplyIntelAgeBand.LongAged) {
      totals.longAgedUnits += row.units;
    }
    if (row.category === null) {
      totals.noCategoryUnits += row.units;
    }
    addPool(totals, row);
    addGradeStatus(totals, row);
    addNextStep(totals, row, config);
    addActionable(totals, row, config);
    if (row.listPrice === null) {
      totals.unpricedUnits += row.units;
    } else {
      addValue(valueByCurrency, row.currency, row.units * row.listPrice);
      totals.valueAtListUsd += supplyIntelValueUsd(row, config) ?? 0;
    }
  }
  return { ...totals, valueAtList: valueList(valueByCurrency) };
}

/** One brand's row, from its unit rows. */
export function rollUpSupplyIntelBrand(
  brand: Pick<SupplyIntelBrandRow, "accountId" | "brandName" | "teamId">,
  rows: readonly SupplyIntelBrandUnitRow[],
  config: SupplyIntelConfig,
): SupplyIntelBrandRow {
  return { ...brand, ...totalSupplyIntelUnitRows(rows, config) };
}

/** One brand's list row, with its totals also split by warehouse. */
export function rollUpSupplyIntelBrandListRow(
  brand: Pick<SupplyIntelBrandRow, "accountId" | "brandName" | "teamId">,
  rows: readonly SupplyIntelBrandUnitRow[],
  config: SupplyIntelConfig,
): SupplyIntelBrandListRow {
  const byWarehouse = new Map<string, SupplyIntelBrandUnitRow[]>();
  for (const row of rows) {
    const warehouseId = row.warehouseId ?? SUPPLY_INTEL_NO_WAREHOUSE;
    const warehouseRows = byWarehouse.get(warehouseId);
    if (warehouseRows) {
      warehouseRows.push(row);
    } else {
      byWarehouse.set(warehouseId, [row]);
    }
  }
  return {
    ...rollUpSupplyIntelBrand(brand, rows, config),
    byWarehouse: [...byWarehouse].map(([warehouseId, warehouseRows]) => ({
      warehouseId,
      ...totalSupplyIntelUnitRows(warehouseRows, config),
    })),
  };
}

/** Totals across brands or warehouses; `valueAtList` stays one value per currency. */
export function summarizeSupplyIntelBrands(
  rows: readonly SupplyIntelUnitTotals[],
): SupplyIntelUnitTotals {
  const totals = emptyTotals();
  const valueByCurrency = new Map<string | null, number>();
  for (const row of rows) {
    totals.units += row.units;
    totals.resaleUnits += row.resaleUnits;
    totals.actionableValueUsd += row.actionableValueUsd;
    totals.liquidationFloorUsd += row.liquidationFloorUsd;
    totals.resaleBufferUsd += row.resaleBufferUsd;
    totals.resaleBufferUsd30 += row.resaleBufferUsd30;
    totals.resaleBufferUsd50 += row.resaleBufferUsd50;
    totals.redoMarketplaceResaleBufferUsd += row.redoMarketplaceResaleBufferUsd;
    totals.redoMarketplaceResaleBufferUsd30 +=
      row.redoMarketplaceResaleBufferUsd30;
    totals.redoMarketplaceResaleBufferUsd50 +=
      row.redoMarketplaceResaleBufferUsd50;
    totals.lotOrLiquidateUnits += row.lotOrLiquidateUnits;
    totals.keepOnBrandStoreUnits += row.keepOnBrandStoreUnits;
    totals.needsReviewUnits += row.needsReviewUnits;
    totals.alreadyRoutedUnits += row.alreadyRoutedUnits;
    totals.excludedUnits += row.excludedUnits;
    totals.noCategoryUnits += row.noCategoryUnits;
    totals.inProcessUnits += row.inProcessUnits;
    totals.agedUnits += row.agedUnits;
    totals.longAgedUnits += row.longAgedUnits;
    totals.forSaleUnits += row.forSaleUnits;
    totals.heldUnits += row.heldUnits;
    totals.damagedAreaUnits += row.damagedAreaUnits;
    totals.notSoldAsNewUnits += row.notSoldAsNewUnits;
    totals.needsGradeUnits += row.needsGradeUnits;
    totals.rejectedUnits += row.rejectedUnits;
    totals.unpricedUnits += row.unpricedUnits;
    totals.valueAtListUsd += row.valueAtListUsd;
    for (const { currency, amount } of row.valueAtList) {
      addValue(valueByCurrency, currency, amount);
    }
  }
  return { ...totals, valueAtList: valueList(valueByCurrency) };
}

function matchesNumber(
  actual: number,
  operator: NumberFilterOperator,
  value: NumberValue,
): boolean {
  const [first, second] = value;
  switch (operator) {
    case NumberFilterOperator.EQUAL:
      return actual === first;
    case NumberFilterOperator.NOT_EQUAL:
      return actual !== first;
    case NumberFilterOperator.LESS_THAN:
      return actual < first;
    case NumberFilterOperator.LESS_THAN_OR_EQUAL:
      return actual <= first;
    case NumberFilterOperator.GREATER_THAN:
      return actual > first;
    case NumberFilterOperator.GREATER_THAN_OR_EQUAL:
      return actual >= first;
    case NumberFilterOperator.BETWEEN:
      return (
        second !== undefined &&
        actual >= Math.min(first, second) &&
        actual <= Math.max(first, second)
      );
  }
}

const NUMBER_FILTER_FIELDS: Partial<
  Record<string, (row: SupplyIntelBrandRow) => number>
> = {
  [SupplyIntelBrandFilterName.AgedUnits]: (row) => row.agedUnits,
  [SupplyIntelBrandFilterName.HeldUnits]: (row) => row.heldUnits,
  [SupplyIntelBrandFilterName.LongAgedUnits]: (row) => row.longAgedUnits,
};

/** A filter without a value yet (still being edited) matches every row. */
function matchesFilter(
  row: SupplyIntelBrandRow,
  filter: AdvancedFilterData,
): boolean {
  if (filter.type === AdvancedFilterType.NUMBER) {
    const field = NUMBER_FILTER_FIELDS[filter.name];
    if (!field || !filter.value) {
      return true;
    }
    return matchesNumber(field(row), filter.operator, filter.value);
  }
  if (
    filter.type === AdvancedFilterType.ARRAY_TO_STRING &&
    filter.name === SupplyIntelBrandFilterName.Currency
  ) {
    if (!filter.value || filter.value.length === 0) {
      return true;
    }
    const wanted = new Set(filter.value);
    const hasWanted = row.valueAtList.some(
      ({ currency }) => currency !== null && wanted.has(currency),
    );
    if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
      return hasWanted;
    }
    if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
      return !hasWanted;
    }
    return true;
  }
  return true;
}

function compareRows(
  a: SupplyIntelBrandRow,
  b: SupplyIntelBrandRow,
  key: SupplyIntelBrandSortKey,
): number {
  if (key === SupplyIntelBrandSortKey.BrandName) {
    return (a.brandName ?? "").localeCompare(b.brandName ?? "");
  }
  return a[key] - b[key];
}

export interface SupplyIntelBrandQuery {
  search?: string;
  advancedFiltersData: readonly AdvancedFilterData[];
  sort: TableSort<SupplyIntelBrandSortKey>;
  page: number;
  pageSize: number;
}

export interface SupplyIntelBrandPage {
  rows: SupplyIntelBrandRow[];
  totalCount: number;
  summary: SupplyIntelUnitTotals;
}

/** Whether a warehouse filter keeps a warehouse; one without a value yet keeps every warehouse. */
export function keepsSupplyIntelWarehouse(
  filter: AdvancedFilterData,
  warehouseId: string,
): boolean {
  if (
    filter.type !== AdvancedFilterType.ARRAY_TO_STRING ||
    !filter.value ||
    filter.value.length === 0
  ) {
    return true;
  }
  if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
    return filter.value.includes(warehouseId);
  }
  if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
    return !filter.value.includes(warehouseId);
  }
  return true;
}

/**
 * The brand's totals over the warehouses every warehouse filter keeps; null
 * when none of its units are in them. Without warehouse filters, its whole row.
 */
function scopeToWarehouses(
  { byWarehouse, ...brand }: SupplyIntelBrandListRow,
  filters: readonly AdvancedFilterData[],
): SupplyIntelBrandRow | null {
  const selected = byWarehouse.filter(({ warehouseId }) =>
    filters.every((filter) => keepsSupplyIntelWarehouse(filter, warehouseId)),
  );
  if (selected.length === byWarehouse.length) {
    return brand;
  }
  if (selected.length === 0) {
    return null;
  }
  return {
    accountId: brand.accountId,
    brandName: brand.brandName,
    teamId: brand.teamId,
    brandPricing: brand.brandPricing,
    ...summarizeSupplyIntelBrands(selected),
  };
}

/**
 * Search, filter, sort and page the brand list; the summary covers every
 * matching brand. A warehouse filter scopes every brand's totals to those
 * warehouses before the other filters run.
 */
export function querySupplyIntelBrands(
  listRows: readonly SupplyIntelBrandListRow[],
  query: SupplyIntelBrandQuery,
): SupplyIntelBrandPage {
  const warehouseFilters = query.advancedFiltersData.filter(
    (filter) => filter.name === SupplyIntelBrandFilterName.Warehouse,
  );
  const rows = listRows.flatMap((row) => {
    const scoped = scopeToWarehouses(row, warehouseFilters);
    return scoped ? [scoped] : [];
  });
  const search = query.search?.trim().toLowerCase();
  const matching = rows.filter(
    (row) =>
      (!search ||
        (row.brandName ?? "").toLowerCase().includes(search) ||
        row.accountId === search) &&
      query.advancedFiltersData.every((filter) => matchesFilter(row, filter)),
  );
  const direction = query.sort.direction === SortDirection.ASC ? 1 : -1;
  const sorted = [...matching].sort(
    (a, b) =>
      direction * compareRows(a, b, query.sort.key) ||
      (a.brandName ?? "").localeCompare(b.brandName ?? "") ||
      a.accountId.localeCompare(b.accountId),
  );
  const start = query.page * query.pageSize;
  return {
    rows: sorted.slice(start, start + query.pageSize),
    totalCount: matching.length,
    summary: summarizeSupplyIntelBrands(matching),
  };
}
